var __typeError = (msg) => {
  throw TypeError(msg);
};
var __accessCheck = (obj, member, msg) => member.has(obj) || __typeError("Cannot " + msg);
var __privateGet = (obj, member, getter) => (__accessCheck(obj, member, "read from private field"), getter ? getter.call(obj) : member.get(obj));
var __privateAdd = (obj, member, value) => member.has(obj) ? __typeError("Cannot add the same private member more than once") : member instanceof WeakSet ? member.add(obj) : member.set(obj, value);
var __privateSet = (obj, member, value, setter) => (__accessCheck(obj, member, "write to private field"), setter ? setter.call(obj, value) : member.set(obj, value), value);
var __privateMethod = (obj, member, method) => (__accessCheck(obj, member, "access private method"), method);
var _summaryThreshold, _historyAbortController, _handleHistoryOpen, _handleHistoryClose, _handlePopState, _handleCartAdd, _CartDrawerComponent_instances, announceCartCount_fn, updateStickyState_fn;
import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from "@theme/dialog";
import { CartAddEvent } from "@theme/events";
import { isMobileBreakpoint } from "@theme/utilities";
class CartDrawerComponent extends DialogComponent {
  constructor() {
    super(...arguments);
    __privateAdd(this, _CartDrawerComponent_instances);
    /** @type {number} */
    __privateAdd(this, _summaryThreshold, 0.5);
    /** @type {AbortController | null} */
    __privateAdd(this, _historyAbortController, null);
    __privateAdd(this, _handleHistoryOpen, () => {
      if (!isMobileBreakpoint()) return;
      if (!history.state?.cartDrawerOpen) {
        history.pushState({ cartDrawerOpen: true }, "");
      }
      __privateSet(this, _historyAbortController, new AbortController());
      window.addEventListener("popstate", __privateGet(this, _handlePopState), { signal: __privateGet(this, _historyAbortController).signal });
    });
    __privateAdd(this, _handleHistoryClose, () => {
      __privateGet(this, _historyAbortController)?.abort();
      if (history.state?.cartDrawerOpen) {
        history.back();
      }
    });
    __privateAdd(this, _handlePopState, async () => {
      if (this.refs.dialog?.open) {
        this.refs.dialog.style.setProperty("--dialog-drawer-closing-animation", "none");
        await this.closeDialog();
        this.refs.dialog.style.removeProperty("--dialog-drawer-closing-animation");
      }
    });
    /**
     * Handles cart add events - opens drawer if auto-open and announces count when open.
     * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
     */
    __privateAdd(this, _handleCartAdd, (event) => {
      if (this.hasAttribute("auto-open")) {
        this.showDialog();
      }
      __privateMethod(this, _CartDrawerComponent_instances, announceCartCount_fn).call(this, event.detail.resource?.item_count);
    });
  }
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, __privateGet(this, _handleCartAdd));
    this.addEventListener(DialogOpenEvent.eventName, __privateMethod(this, _CartDrawerComponent_instances, updateStickyState_fn));
    this.addEventListener(DialogOpenEvent.eventName, __privateGet(this, _handleHistoryOpen));
    this.addEventListener(DialogCloseEvent.eventName, __privateGet(this, _handleHistoryClose));
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, "");
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, __privateGet(this, _handleCartAdd));
    this.removeEventListener(DialogOpenEvent.eventName, __privateMethod(this, _CartDrawerComponent_instances, updateStickyState_fn));
    this.removeEventListener(DialogOpenEvent.eventName, __privateGet(this, _handleHistoryOpen));
    this.removeEventListener(DialogCloseEvent.eventName, __privateGet(this, _handleHistoryClose));
    __privateGet(this, _historyAbortController)?.abort();
  }
  open() {
    this.showDialog();
    customElements.whenDefined("shopify-payment-terms").then(() => {
      const installmentsContent = document.querySelector("shopify-payment-terms")?.shadowRoot;
      const cta = installmentsContent?.querySelector("#shopify-installments-cta");
      cta?.addEventListener("click", this.closeDialog, { once: true });
    });
  }
  close() {
    this.closeDialog();
  }
}
_summaryThreshold = new WeakMap();
_historyAbortController = new WeakMap();
_handleHistoryOpen = new WeakMap();
_handleHistoryClose = new WeakMap();
_handlePopState = new WeakMap();
_handleCartAdd = new WeakMap();
_CartDrawerComponent_instances = new WeakSet();
/**
 * Announces cart count to screen readers when dialog is open.
 * @param {number | undefined} cartCount
 */
announceCartCount_fn = function(cartCount) {
  const liveRegion = (
    /** @type {HTMLElement | undefined} */
    this.refs.liveRegion
  );
  if (!this.refs.dialog?.open || !liveRegion || cartCount === void 0) return;
  liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
};
updateStickyState_fn = function() {
  const { dialog } = (
    /** @type {Refs} */
    this.refs
  );
  if (!dialog) return;
  const content = dialog.querySelector(".cart-drawer__content");
  const summary = dialog.querySelector(".cart-drawer__summary");
  if (!content || !summary) {
    dialog.setAttribute("cart-summary-sticky", "false");
    return;
  }
  const drawerHeight = dialog.getBoundingClientRect().height;
  const summaryHeight = summary.getBoundingClientRect().height;
  const ratio = summaryHeight / drawerHeight;
  dialog.setAttribute("cart-summary-sticky", ratio > __privateGet(this, _summaryThreshold) ? "false" : "true");
};
if (!customElements.get("cart-drawer-component")) {
  customElements.define("cart-drawer-component", CartDrawerComponent);
}
